import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Hash, Send, ChevronDown, Bold, Italic, Link, Smile, Paperclip, AtSign } from "lucide-react";
import { logger } from "../../utils/logger";
import { scormService } from "../../utils/scormService";
import { ChatMessageContent } from "./ChatMessageContent";
import { OtpNotification } from "./OtpNotification";
import { FakeLandingPage } from "./FakeLandingPage";
import type { LandingPage, LandingPageSubmission } from "./FakeLandingPage";
import { SmishingInputHelper } from "./SmishingInputHelper";
import { buildSmishingInitRequest, buildSmishingMessageRequest, type SmishingChatInitResponse, type SmishingChatReplyResponse, type SmishingRequestCategory } from "./smishing-chat-api";

// Slack-style typing dots animation
function TypingIndicator({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5 rounded-full bg-white/10 px-2 py-1">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60 [animation-delay:0ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60 [animation-delay:150ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60 [animation-delay:300ms]" />
      </div>
      <span className="text-[12px] text-white/50">{name} is typing...</span>
    </div>
  );
}

type SlackStatus = "idle" | "sending" | "receiving" | "ending" | "error" | "finished";

interface SlackMessage {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

interface SlackChatProps {
  microlearningId: string;
  language?: string;
  endpoint?: string;
  workspaceName?: string;
  channelName?: string;
  senderName?: string;
  senderNumber?: string;
  startLabel?: string;
  statusTexts?: Partial<Record<SlackStatus, string>>;
  linkWarningTitle?: string;
  linkWarningBody?: string;
  otpTexts?: { codeLabel?: string; codeSuffix?: string; backLabel?: string; backHint?: string };
  landingPageTexts?: { backLabel?: string };
  uiTexts?: {
    now?: string;
    inputPlaceholder?: string;
    online?: string;
    typing?: string;
    unknownSender?: string;
    startChat?: string;
    chatEnded?: string;
    errorGeneric?: string;
    errorStartChat?: string;
    errorGetReply?: string;
  };
  embedded?: boolean;
  className?: string;
  onStatusChange?: (status: SlackStatus) => void;
  onCompletionChange?: (completed: boolean) => void;
}

const FALLBACK_SMISHING_ENDPOINT = "https://agentic-ai-agent.keepnetlabs.com/smishing/chat";
const DEFAULT_SMISHING_ENDPOINT = typeof window !== "undefined" && (
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
) ? "http://localhost:4111/smishing/chat" : FALLBACK_SMISHING_ENDPOINT;

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("accessToken");
};

const formatTime = () => {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export function SlackChat({
  microlearningId,
  language = "en-gb",
  endpoint = DEFAULT_SMISHING_ENDPOINT,
  workspaceName = "Workspace",
  channelName = "general",
  senderName,
  statusTexts,
  linkWarningTitle,
  linkWarningBody,
  otpTexts,
  landingPageTexts,
  uiTexts,
  embedded = false,
  className = "",
  onStatusChange,
  onCompletionChange
}: SlackChatProps) {
  const [status, setStatus] = useState<SlackStatus>("idle");
  const [messages, setMessages] = useState<SlackMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [hasStarted, setHasStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otpData, setOtpData] = useState<{ code: string; sender: string } | null>(null);
  const [expectedResponseCategory, setExpectedResponseCategory] = useState<SmishingRequestCategory | undefined>(undefined);
  const shownOtpCodes = useRef<Set<string>>(new Set());
  const activeOtpRef = useRef<{ code: string; sender: string } | null>(null);
  const [activeLandingPage, setActiveLandingPage] = useState<LandingPage | null>(null);
  const landingPageDataRef = useRef<LandingPage | null>(null);
  const pendingLandingPageRef = useRef<LandingPageSubmission | null>(null);
  const clickedLinkRef = useRef<string | null>(null);
  const autoSendRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const initRequestedRef = useRef(false);

  // Get user info from SCORM LMS
  const studentInfo = useMemo(() => scormService.getStudentInfo(), []);
  const resolvedSenderName = senderName || uiTexts?.unknownSender || "Unknown";
  const userName = studentInfo.name || "You";
  const userInitial = userName.charAt(0).toUpperCase();

  const updateStatus = useCallback((next: SlackStatus) => {
    setStatus(next);
    onStatusChange?.(next);
  }, [onStatusChange]);

  const statusLabel = useMemo(() => {
    const defaultLabels: Record<SlackStatus, string> = {
      idle: uiTexts?.startChat || "Tap to start",
      sending: "Sending\u2026",
      receiving: "Typing\u2026",
      ending: "Ending\u2026",
      finished: uiTexts?.chatEnded || "Chat ended",
      error: uiTexts?.errorGeneric || "Something went wrong"
    };
    return statusTexts?.[status] ?? statusTexts?.idle ?? defaultLabels[status];
  }, [status, statusTexts, uiTexts]);

  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const initChat = useCallback(async () => {
    if (hasStarted || isInitializing) return;
    setIsInitializing(true);
    updateStatus("sending");
    setErrorMessage(null);
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingInitRequest(microlearningId, language, "slack"))
      });
      const data = await response.json() as SmishingChatInitResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Init failed: ${response.status}`);
      }
      if (data.firstMessage) {
        setMessages([{ role: "assistant" as const, content: data.firstMessage, timestamp: formatTime() }]);
      }
      // Build-time OTP: show notification shortly after chat opens
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      if (data.landingPage) {
        landingPageDataRef.current = data.landingPage;
      }
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        setHasStarted(true);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorStartChat || "Failed to start chat.");
      logger.push({
        level: "error",
        code: "SLACK_INIT_FAIL",
        message: "Failed to init slack chat",
        detail: String(error)
      });
    } finally {
      setIsInitializing(false);
    }
  }, [endpoint, hasStarted, isInitializing, language, microlearningId, onCompletionChange, uiTexts?.errorStartChat, updateStatus]);

  useEffect(() => {
    if (initRequestedRef.current) return;
    initRequestedRef.current = true;
    initChat();
  }, [initChat]);

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || status !== "idle" || !hasStarted || isFinished) return;
    const nextMessages: SlackMessage[] = [...messages, { role: "user" as const, content: trimmed, timestamp: formatTime() }];
    setMessages(nextMessages);
    setInputValue("");
    setExpectedResponseCategory(undefined);
    updateStatus("receiving");
    setErrorMessage(null);

    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingMessageRequest({
          microlearningId,
          language,
          channel: "slack",
          messages: nextMessages,
          pendingOtp: activeOtpRef.current,
          pendingLandingPageSubmission: pendingLandingPageRef.current,
          pendingClickedLink: clickedLinkRef.current,
        }))
      });
      pendingLandingPageRef.current = null;
      clickedLinkRef.current = null;
      const data = await response.json() as SmishingChatReplyResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Chat failed: ${response.status}`);
      }
      const replyParts = data.replies && data.replies.length > 1 ? data.replies : data.reply ? [data.reply] : [];
      if (replyParts.length > 0) {
        // First message immediately
        let accumulated: SlackMessage[] = [...nextMessages, { role: "assistant" as const, content: replyParts[0], timestamp: formatTime() }];
        setMessages(accumulated);

        // Subsequent messages with staggered delay (typing effect)
        for (let i = 1; i < replyParts.length; i++) {
          updateStatus("receiving");
          await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1500));
          accumulated = [...accumulated, { role: "assistant" as const, content: replyParts[i], timestamp: formatTime() }];
          setMessages(accumulated);
        }
      }
      // Trigger OTP notification if backend sent one
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      // Store landing page data — shown only when user taps the link
      landingPageDataRef.current = data.landingPage || null;
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorGetReply || "Failed to get reply.");
      logger.push({
        level: "error",
        code: "SLACK_CHAT_FAIL",
        message: "Failed to fetch slack reply",
        detail: String(error)
      });
    }
  }, [endpoint, hasStarted, inputValue, isFinished, language, messages, microlearningId, onCompletionChange, status, uiTexts?.errorGetReply, updateStatus]);

  const handleLinkClick = useCallback((url: string): boolean => {
    clickedLinkRef.current = url;
    if (landingPageDataRef.current && landingPageDataRef.current.url === url) {
      setActiveLandingPage(landingPageDataRef.current);
      return true;
    }
    return false;
  }, []);

  const handleLandingPageSubmit = useCallback((submission: LandingPageSubmission) => {
    pendingLandingPageRef.current = submission;
    clickedLinkRef.current = null;
    setActiveLandingPage(null);
    setInputValue(Object.values(submission.fields).join(", "));
    autoSendRef.current = true;
  }, []);

  const handleLandingPageBack = useCallback(() => {
    setActiveLandingPage(null);
  }, []);

  // Auto-send after landing page form submission
  useEffect(() => {
    if (autoSendRef.current && inputValue.trim()) {
      autoSendRef.current = false;
      sendMessage();
    }
  }, [inputValue, sendMessage]);

  const isBusy = status === "sending" || status === "receiving" || isInitializing || isFinished;
  const previewText = messages[0]?.content || statusLabel;
  const wrapperClassName = embedded
    ? `w-full ${className}`
    : `w-full max-w-[420px] glass-border-3 p-4 ${className}`;
  const chatShellClassName = embedded
    ? "flex h-full min-h-0 flex-col overflow-hidden rounded-lg"
    : "flex h-full min-h-[420px] flex-col overflow-hidden rounded-lg";

  // Slack colors
  const slackPurple = "#4A154B";
  const slackSidebar = "#3F0E40";

  return (
    <div className={wrapperClassName}>
      {!isOpen ? (
        <div className="relative flex min-h-[380px] flex-col items-center justify-center">
          {/* Slack-style notification card */}
          <button
            type="button"
            onClick={() => {
              setIsOpen(true);
              if (!hasStarted) {
                initChat();
              }
            }}
            className="group relative w-full max-w-[270px] overflow-hidden rounded-[18px] border border-white/30 bg-white/80 p-3 text-left shadow-[0_4px_24px_rgba(0,0,0,0.08)] backdrop-blur-xl transition-all duration-200 hover:scale-[1.01] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] active:scale-[0.99] dark:border-white/10 dark:bg-[#2C2C2E]/90 dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
          >
            {/* Header: Slack icon + Slack + now */}
            <div className="relative flex items-center gap-2">
              {/* Slack app icon */}
              <div
                className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] shadow-sm"
                style={{ backgroundColor: slackPurple }}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="white">
                  <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
                </svg>
              </div>
              <span className="text-[13px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                Slack
              </span>
              <span className="ml-auto text-[12px] text-[#8E8E93] dark:text-[#98989D]">
                {uiTexts?.now || "now"}
              </span>
            </div>

            {/* Channel name & message */}
            <div className="relative mt-1.5 pl-[44px]">
              <div className="flex items-center gap-1">
                <Hash size={12} className="text-[#8E8E93]" />
                <span className="text-[14px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                  {channelName}
                </span>
                {/* Unread badge */}
                <span className="ml-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#E01E5A] px-1.5 text-[11px] font-bold text-white">
                  1
                </span>
              </div>
              {/* Message preview */}
              <div className="mt-0.5 pr-3 text-[14px] leading-snug text-[#3C3C43]/70 line-clamp-2 dark:text-[#EBEBF5]/60">
                <span className="font-medium">{resolvedSenderName}:</span> {previewText}
              </div>
            </div>
          </button>

          {/* Home indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <div className="h-1 w-28 rounded-full bg-black/15 dark:bg-white/20" />
          </div>
        </div>
      ) : (
        <div className={`${chatShellClassName} relative`} style={{ backgroundColor: "#1A1D21" }}>
          {/* OTP Notification overlay */}
          {otpData && (
            <OtpNotification
              otpCode={otpData.code}
              otpSender={otpData.sender}
              codeLabel={otpTexts?.codeLabel}
              codeSuffix={otpTexts?.codeSuffix}
              backLabel={otpTexts?.backLabel}
              backHint={otpTexts?.backHint}
              nowLabel={uiTexts?.now}
            />
          )}
          {activeLandingPage && (
            <FakeLandingPage
              landingPage={activeLandingPage}
              onSubmit={handleLandingPageSubmit}
              onBack={handleLandingPageBack}
              backLabel={landingPageTexts?.backLabel}
            />
          )}
          {/* Workspace header bar */}
          <div
            className="shrink-0 flex items-center justify-between px-3 py-1.5"
            style={{ backgroundColor: slackSidebar }}
          >
            <div className="flex items-center gap-1">
              <span className="text-[13px] font-bold text-white">
                {workspaceName}
              </span>
              <ChevronDown size={14} className="text-white/50" />
            </div>
            {/* Profile avatar with online status */}
            <div className="relative">
              <div className="h-7 w-7 rounded-md bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-[11px] font-bold shadow-sm">
                {userInitial}
              </div>
              {/* Online indicator */}
              <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-[#3F0E40] bg-emerald-400" />
            </div>
          </div>

          {/* Channel header */}
          <div
            className="shrink-0 flex items-center gap-2 border-b border-white/10 px-3 py-2"
            style={{ backgroundColor: "#1A1D21" }}
          >
            <Hash size={16} className="text-white/50" />
            <span className="text-[14px] font-bold text-white">
              {channelName}
            </span>
          </div>

          {/* Messages area */}
          <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-2" style={{ backgroundColor: "#1A1D21" }}>
            {messages.length === 0 && (
              <div className="flex items-center justify-center py-8 text-xs text-white/50">
                {statusLabel}
              </div>
            )}
            <div className="flex flex-col gap-3">
              {messages.map((msg, idx) => (
                <div key={`${msg.role}-${idx}`} className="flex gap-2">
                  {/* Avatar */}
                  <div
                    className={`h-8 w-8 shrink-0 rounded-md flex items-center justify-center text-white text-xs font-bold ${msg.role === "assistant" ? "bg-gradient-to-br from-amber-500 to-orange-600" : "bg-gradient-to-br from-blue-500 to-indigo-600"}`}
                  >
                    {msg.role === "assistant" ? resolvedSenderName.charAt(0).toUpperCase() : userInitial}
                  </div>
                  {/* Message content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[13px] font-bold text-white">
                        {msg.role === "assistant" ? resolvedSenderName : userName}
                      </span>
                      {/* APP badge for bot/sender */}
                      {msg.role === "assistant" && (
                        <span className="rounded-[3px] bg-white/10 px-1 py-[1px] text-[10px] font-medium uppercase leading-none text-white/50">
                          app
                        </span>
                      )}
                      <span className="text-[11px] text-white/40">
                        {msg.timestamp}
                      </span>
                    </div>
                    <p className="text-[14px] leading-snug text-white/90 break-words [overflow-wrap:anywhere] text-left">
                      <ChatMessageContent
                        content={msg.content}
                        linkWarningTitle={linkWarningTitle}
                        linkWarningBody={linkWarningBody}
                        onLinkClick={handleLinkClick}
                      />
                    </p>
                    {/* Reactions - show on first assistant message */}
                    {msg.role === "assistant" && idx === 0 && (
                      <div className="mt-1 flex gap-1">
                        <span className="inline-flex items-center gap-0.5 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[11px]">
                          <span>&#128064;</span>
                          <span className="text-white/50">2</span>
                        </span>
                        <span className="inline-flex items-center gap-0.5 rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[11px]">
                          <span>&#128077;</span>
                          <span className="text-white/50">1</span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {status === "receiving" && (
                <TypingIndicator name={resolvedSenderName} />
              )}
            </div>
          </div>

          {/* Input area */}
          <SmishingInputHelper
            category={expectedResponseCategory}
            language={language}
            disabled={isBusy || !hasStarted}
            onSelect={setInputValue}
          />
          <div className="shrink-0 px-3 pb-2 pt-2" style={{ backgroundColor: "#1A1D21" }}>
            <div className="overflow-hidden rounded-lg border border-white/20 bg-white/5">
              {/* Input field */}
              <div className="flex items-center gap-2 px-3 py-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  onKeyDown={(event) => event.key === "Enter" && sendMessage()}
                  placeholder={hasStarted ? (uiTexts?.inputPlaceholder || `Message #${channelName}`) : statusLabel}
                  disabled={isBusy || !hasStarted}
                  className="flex-1 bg-transparent text-[14px] text-white placeholder-white/40 focus:outline-none"
                />
              </div>
              {/* Formatting toolbar */}
              <div className="flex items-center justify-between border-t border-white/10 px-2 py-1">
                <div className="flex items-center gap-0.5">
                  <span className="flex h-6 w-6 items-center justify-center rounded text-white/30">
                    <Bold size={14} />
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center rounded text-white/30">
                    <Italic size={14} />
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center rounded text-white/30">
                    <Link size={14} />
                  </span>
                  <span className="mx-0.5 h-4 w-px bg-white/10" />
                  <span className="flex h-6 w-6 items-center justify-center rounded text-white/30">
                    <AtSign size={14} />
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center rounded text-white/30">
                    <Smile size={14} />
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center rounded text-white/30">
                    <Paperclip size={14} />
                  </span>
                </div>
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={!inputValue.trim() || isBusy || !hasStarted}
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors ${
                    inputValue.trim() && !isBusy && hasStarted
                      ? "bg-[#007A5A] text-white"
                      : "text-white/30"
                  } disabled:opacity-40`}
                  aria-label="Send message"
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mt-2 text-xs text-rose-600 dark:text-rose-300">{errorMessage}</div>
      )}
    </div>
  );
}
